import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

interface FacilityResultItemProps {
  facility: Facility;
  selected: boolean;
  expanded: boolean;
  isLoadingShifts: boolean;
  copied?: boolean;
  onClick: () => void;
}

export const FacilityResultItem = forwardRef<HTMLDivElement, FacilityResultItemProps>(
  (props, ref) => {
    const {
      facility,
      selected,
      expanded,
      isLoadingShifts,
      copied: copiedFromKeyboard = false,
      onClick,
    } = props;
    const [copiedFromClick, setCopiedFromClick] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
      () => () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
      },
      []
    );

    const isCopied = copiedFromKeyboard || copiedFromClick;

    const handleCopy = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        navigator.clipboard.writeText(facility.userId).then(
          () => {
            if (timerRef.current !== null) {
              clearTimeout(timerRef.current);
            }
            setCopiedFromClick(true);
            timerRef.current = setTimeout(() => {
              setCopiedFromClick(false);
            }, 2000);
          },
          () => {
            showErrorToast("Failed to copy to clipboard");
          }
        );
      },
      [facility.userId]
    );

    return (
      <ListItemButton
        ref={ref}
        selected={selected}
        sx={{
          py: 1,
          px: 2,
          "&.Mui-selected": {
            backgroundColor: "action.selected",
          },
        }}
        onClick={onClick}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <BusinessIcon color="secondary" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Text sx={{ fontWeight: 500 }}>{facility.name}</Text>
              {selected &&
                (expanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                ))}
              {isLoadingShifts && expanded && <CircularProgress size={14} />}
            </Box>
          }
          secondary={`${facility.type} • ${facility.status}`}
          secondaryTypographyProps={{ variant: "caption" }}
        />
        <Tooltip title={isCopied ? "Copied!" : "Copy ID"}>
          <IconButton size="small" color={isCopied ? "success" : "default"} onClick={handleCopy}>
            {isCopied ? (
              <CheckIcon sx={{ fontSize: 16 }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </ListItemButton>
    );
  }
);

FacilityResultItem.displayName = "FacilityResultItem";
